import React, { useState, useRef, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';

const SHOW_DELAY_MS = 150;

function mergeRefs(node, childRef) {
  if (typeof childRef === 'function') childRef(node);
  else if (childRef) childRef.current = node;
}

/**
 * Hover tooltip with auto-flip (above/below) relative to a scroll boundary.
 */
export default function Tooltip({ content, children, boundaryRef }) {
  const [visible, setVisible] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, placement: 'below' });
  const triggerRef = useRef(null);
  const showTimerRef = useRef(null);

  const updatePosition = useCallback(() => {
    const el = triggerRef.current;
    if (!el) return;

    const rect = el.getBoundingClientRect();
    const boundary = boundaryRef?.current;
    const boundaryRect = boundary?.getBoundingClientRect() ?? {
      top: 0,
      bottom: window.innerHeight,
    };

    const spaceBelow = boundaryRect.bottom - rect.bottom;
    const spaceAbove = rect.top - boundaryRect.top;
    const placement = spaceBelow < 140 && spaceAbove > spaceBelow ? 'above' : 'below';

    setCoords({
      top: placement === 'above' ? rect.top - 8 : rect.bottom + 8,
      left: rect.left + rect.width / 2,
      placement,
    });
  }, [boundaryRef]);

  const handleMouseEnter = () => {
    showTimerRef.current = setTimeout(() => {
      updatePosition();
      setVisible(true);
    }, SHOW_DELAY_MS);
  };

  const handleMouseLeave = () => {
    if (showTimerRef.current) {
      clearTimeout(showTimerRef.current);
      showTimerRef.current = null;
    }
    setVisible(false);
  };

  useEffect(() => () => {
    if (showTimerRef.current) clearTimeout(showTimerRef.current);
  }, []);

  useEffect(() => {
    if (!visible) return undefined;
    const reposition = () => updatePosition();
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
    };
  }, [visible, updatePosition]);

  const child = React.Children.only(children);
  const transform = coords.placement === 'above' ? 'translate(-50%, -100%)' : 'translate(-50%, 0)';

  return (
    <>
      {React.cloneElement(child, {
        ref: (node) => {
          triggerRef.current = node;
          mergeRefs(node, child.ref);
        },
        onMouseEnter: (e) => {
          child.props.onMouseEnter?.(e);
          handleMouseEnter();
        },
        onMouseLeave: (e) => {
          child.props.onMouseLeave?.(e);
          handleMouseLeave();
        },
      })}
      {visible && content && createPortal(
        <div
          role="tooltip"
          style={{
            position: 'fixed',
            top: coords.top,
            left: coords.left,
            transform,
            zIndex: 9999,
            maxWidth: 280,
            backgroundColor: '#1e293b',
            color: '#fff',
            borderRadius: '0.5rem',
            padding: '0.5rem 0.75rem',
            fontSize: '0.75rem',
            lineHeight: 1.5,
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word',
            boxShadow: '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)',
            pointerEvents: 'none',
          }}
        >
          {content}
        </div>,
        document.body,
      )}
    </>
  );
}
